<template>
  <BrandLinksHead
    v-if="brandSettings"
    :title="reportTitle"
    :brand-settings="brandSettings"
    :fav-icon-source="accountSettings.website"
  />
  <div class="blur-sm relative h-screen flex flex-col xl:flex-row w-full max-w-[1920px] mx-auto bg-default-50">
    <div class="w-full xl:max-w-[286px] border-r border-default-200 flex justify-between xl:justify-start xl:flex-col gap-sm z-20 py-base xl:pt-xxl px-large lg:px-large bg-white xl:bg-default-50">
      <div class="hidden xl:flex flex-col gap-y-base min-w-0">
        <h1
          class="titles-branding text-2xl"
          :style="titlesStyling"
        >
          {{ reportContentData.reportTitle }}
        </h1>
        <div
          class="body-text-branding flex flex-col text-sm"
          :style="bodyTextStyling"
        >
          <span
            v-if="reportContentData.address"
            class="truncate"
          >
            {{ reportContentData.address.addressLine1 }}
          </span>
          <span
            v-if="reportContentData.address"
            class="truncate"
          >
            {{ reportContentData.address.addressLine2 }}
          </span>
        </div>
      </div>
      <div class="flex justify-between items-center w-full xl:hidden">
        <SpectreButton disabled>
          Contact Us
        </SpectreButton>
      </div>
    </div>
    <div class="flex-grow flex flex-col overflow-hidden">
      <div class="h-20 flex divide-x items-center">
        <ReportTabs :brand-settings="brandSettings" />
        <div class="hidden xl:flex justify-center items-center flex-shrink-0 px-large py-sm">
          <SpectreButton
            disabled
            class="insights-button"
            :style="primaryButtonStyles"
          >
            Contact Us
          </SpectreButton>
        </div>
      </div>
    </div>
  </div>
  <SpectreModal :open="showModal">
    <SpectreModalContent>
      <img
        v-if="brandSettings.logos.rectangle?.url"
        :src="brandSettings.logos.rectangle.url"
        :alt="`${companyName} logo`"
        class="mb-7 max-w-56 mx-auto"
      >
      <DialogTitle
        class="text-2xl font-bold text-center mb-xxs titles"
        :style="titlesStyling"
      >
        {{ reportContentData.address?.houseNumber }} {{ reportContentData.address?.street }}
      </DialogTitle>
      <DialogDescription
        class="font-medium text-sm text-center mb-7 body-text-branding"
        :style="bodyTextStyling"
      >
        To access the report, please fill in the details below.
      </DialogDescription>
      <form
        class="grid grid-cols-2 gap-4 mb-8"
        @submit.prevent="onSubmit"
      >
        <div
          v-if="accountSettings.leadCaptureFields.name.include"
          class="col-span-1"
        >
          <InsightsFormGroup label="First name">
            <SpectreInput
              v-model="form.first_name"
              :required="accountSettings.leadCaptureFields.name.require"
              :invalid="!!form.errors?.first_name"
              autocomplete="given-name"
            />
            <SpectreErrorMessage v-if="form.errors?.first_name">
              {{ form.errors?.first_name }}
            </SpectreErrorMessage>
          </InsightsFormGroup>
        </div>
        <div
          v-if="accountSettings.leadCaptureFields.name.include"
          class="col-span-1"
        >
          <InsightsFormGroup label="Last name">
            <SpectreInput
              v-model="form.last_name"
              :required="accountSettings.leadCaptureFields.name.require"
              :invalid="!!form.errors?.last_name"
              autocomplete="family-name"
            />
            <SpectreErrorMessage v-if="form.errors?.last_name">
              {{ form.errors?.last_name }}
            </SpectreErrorMessage>
          </InsightsFormGroup>
        </div>
        <div
          v-if="accountSettings.leadCaptureFields.email.include"
          class="col-span-2"
        >
          <InsightsFormGroup label="Email">
            <SpectreInput
              v-model="form.email"
              type="email"
              required
              :invalid="!!form.errors?.email"
              inputmode="email"
              autocomplete="email"
            />
            <SpectreErrorMessage v-if="form.errors?.email">
              {{ form.errors?.email }}
            </SpectreErrorMessage>
          </InsightsFormGroup>
        </div>
        <div
          v-if="accountSettings.leadCaptureFields.contactNumber.include"
          class="col-span-2"
        >
          <InsightsFormGroup label="Contact number">
            <SpectreInput
              v-model="form.contact_number"
              type="tel"
              :required="accountSettings.leadCaptureFields.contactNumber.require"
              :invalid="!!form.errors?.contact_number"
              autocomplete="tel"
              inputmode="tel"
            />
            <SpectreErrorMessage v-if="form.errors?.contact_number">
              {{ form.errors?.contact_number }}
            </SpectreErrorMessage>
          </InsightsFormGroup>
        </div>
        <div
          v-if="accountSettings.leadCaptureFields.address.include"
          class="col-span-2"
        >
          <InsightsFormGroup label="Address">
            <SpectreInputAddressSearch
              v-model="selectedProperty"
              :config="{
                hostUrl: page.props.config.kraken.search_url,
                apiKey: page.props.config.kraken.api_key,
              }"
              :invalid="!!form.errors?.street_group_property_id"
              placeholder="Start typing your address"
            />
            <SpectreErrorMessage v-if="form.errors?.street_group_property_id">
              {{ form.errors?.street_group_property_id }}
            </SpectreErrorMessage>
          </InsightsFormGroup>
        </div>
        <div class="col-span-2">
          <SpectreField class="flex space-x-3 items-center">
            <SpectreCheckbox v-model="form.has_marketing_consent" />
            <SpectreLabel>
              Yes, I would like to receive marketing communications.
            </SpectreLabel>
          </SpectreField>
        </div>
        <div class="col-span-2">
          <SpectreButton
            type="submit"
            block
            size="xl"
            :loading="form.processing"
            class="insights-button"
            :style="primaryButtonStyles"
          >
            View the report
          </SpectreButton>
        </div>
      </form>
      <p class="text-default-400 text-center text-xs">
        Please note that this report is provided by Spectre Property Reports who will send the above details to
        <a
          href="https://streetgroup.co.uk"
          target="_blank"
          class="underline"
        >
          {{ companyName }}</a>
        only. By submitting, you agree to the
        <a
          href="https://street.co.uk/terms"
          class="underline"
        >
          terms and conditions</a>
        and our privacy policy.
      </p>
    </SpectreModalContent>
  </SpectreModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { useStorage, useToggle } from '@vueuse/core';
import initialiseGtm from '@/Support/InitialiseGtm';
import collect from 'collect.js';
import ReportTabs from '@/Components/ReportTabs.vue';
import { DialogDescription, DialogTitle } from '@headlessui/vue';
import InsightsFormGroup from '@/Components/InsightsFormGroup.vue';
import BrandLinksHead from '@/Components/BrandLinksHead.vue';
import BlankLayout from '@/Layouts/BlankLayout.vue';
import {
  SpectreButton,
  SpectreCheckbox,
  SpectreLabel,
  SpectreInput,
  SpectreField,
  SpectreErrorMessage,
  SpectreModal,
  SpectreInputAddressSearch,
  SpectreModalContent,
} from '@agentsoftware/spectre-ui';
import BrandSettingsData = App.Data.Settings.BrandSettingsData;
import CustomDomainData = App.Data.CustomDomainData;
import AccountSettingsData = App.Data.Settings.AccountSettingsData;

const props = defineProps<{
  accountSettings: AccountSettingsData;
  brandSettings: BrandSettingsData;
  companyName: string | null;
  customDomain: CustomDomainData;
  report: App.Data.Report.ReportData & {
    id: string;
  };
  reportContentData: App.Data.Report.ReportContentData;
}>();

const page = usePage();

const [showModal] = useToggle(true);

defineOptions({
  layout: BlankLayout,
});

const personalDetails = useStorage<{
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
}>('personal-details', {
  firstName: '',
  lastName: '',
  email: '',
  contactNumber: '',
});

const selectedProperty = ref<string>();

const form = useForm({
  report_id: props.report.id,
  first_name: personalDetails.value.firstName,
  last_name: personalDetails.value.lastName,
  email: personalDetails.value.email,
  contact_number: personalDetails.value.contactNumber,
  street_group_property_id: '',
  has_marketing_consent: false,
});

const domain = computed(() => (props.customDomain ? `https://${props.customDomain.url}` : page.props.app.url));

const reportTitle = computed(() => `Report for ${props.reportContentData.address?.houseNumber} ${props.reportContentData.address?.street}`);

const onSubmit = () => {
  form.street_group_property_id = selectedProperty.value as string;

  personalDetails.value = {
    firstName: form.first_name,
    lastName: form.last_name,
    email: form.email,
    contactNumber: form.contact_number,
  };

  form.post(`${domain.value}/reports/${props.report.id}/leads/create`);
};

const titlesStyling = computed(() => ({
  '--titlesFontFamily': props.brandSettings.text.heading.fontFamily,
  '--titlesFontWeight': props.brandSettings.text.heading.fontWeight,
  '--titlesColour': props.brandSettings.text.heading.colour,
}));

const bodyTextStyling = computed(() => ({
  '--bodyTextFontFamily': props.brandSettings.text.body.fontFamily,
  '--bodyTextFontWeight': props.brandSettings.text.body.fontWeight,
  '--bodyTextColour': props.brandSettings.text.body.colour,
}));

const primaryButtonStyles = computed(() => ({
  '--primaryButtonFontFamily': `${props.brandSettings.buttons.primary.fontFamily}`,
  '--primaryButtonFontWeight': `${props.brandSettings.buttons.primary.fontWeight}`,
  '--primaryButtonTextColour': `${props.brandSettings.buttons.primary.colour}`,
  '--primaryButtonTextHoverColour': `${props.brandSettings.buttons.primary.hoverColour}`,
  '--primaryButtonBackgroundColour': `${props.brandSettings.buttons.primary.backgroundColour}`,
  '--primaryButtonBackgroundHoverColour': `${props.brandSettings.buttons.primary.backgroundHoverColour}`,
}));

onMounted(() => {
  if (typeof window !== 'undefined' && props.accountSettings.googleTagManager && !collect(document.querySelectorAll('script')).pluck('src').contains(`https://www.googletagmanager.com/gtm.js?id=${props.accountSettings.googleTagManager}`)) {
    initialiseGtm(props.accountSettings.googleTagManager);
  }
});
</script>

<style scoped>
.titles {
  font-family: var(--titlesFontFamily);
  font-weight: var(--titlesFontWeight);
  color: var(--titlesColour);
}

.body-text-branding {
  font-family: var(--bodyTextFontFamily);
  font-weight: var(--bodyTextFontWeight);
  color: var(--bodyTextColour);
}

.insights-button {
  font-family: var(--primaryButtonFontFamily);
  font-weight: var(--primaryButtonFontWeight);
  color: var(--primaryButtonTextColour);
  background-color: var(--primaryButtonBackgroundColour);
  border-color: var(--primaryButtonBackgroundColour);
}

.insights-button:hover:not(:disabled) {
  color: var(--primaryButtonTextHoverColour);
  background-color: var(--primaryButtonBackgroundHoverColour);
  border-color: var(--primaryButtonBackgroundHoverColour);
}
</style>
